/**
 * D-124: the learners' review requests at the top of the grading queue. The deadline board
 * (pending, overdue, due soon, average time over 30 days), then the requests of the exam shown,
 * the most urgent first, with the time left before the promised deadline (72 h). The trainer
 * re-marks the work in the queue below if needed, then completes the review with a comment: the
 * learner is told by e-mail. A trainer only sees the exams of their language (D-120, API).
 */

import {
  type AcademyReviewRequestsListParams,
  type TrainerReviewRequest,
  academyReviewRequestsCompleteCreate,
  getAcademyReviewRequestsListQueryKey,
  getAcademyReviewRequestsStatsRetrieveQueryKey,
  useAcademyReviewRequestsList,
  useAcademyReviewRequestsStatsRetrieve,
} from "@agencyos/api-client";
import { formatReviewMark, reviewUrgency } from "@agencyos/core";
import { Badge, Button, GlassCard, StatCard, TextAreaField } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { AlarmClock, CheckCircle2, Hourglass, Timer } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { dateTimeText } from "../billing/shared";
import { useAdminExam } from "./examContext";
import { ErrorList, Pager, useAcademyRoles } from "./shared";

function RequestRow({ row, onDone }: { row: TrainerReviewRequest; onDone: () => Promise<void> }) {
  const { t, i18n } = useTranslation();
  const { canEdit } = useAcademyRoles();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const urgency = reviewUrgency(row.hours_left);
  const pending = row.status === "pending";

  async function complete() {
    setBusy(true);
    setError(null);
    try {
      await academyReviewRequestsCompleteCreate(row.id, { comment });
      await onDone();
    } catch (failure) {
      setError(failure);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="stack review-request">
      <div className="section-title">
        <div>
          <strong>{row.learner_name || row.learner_email}</strong>{" "}
          <span className="muted">
            · {row.skill.toUpperCase()} · {t("academyAdmin.topics.task", { n: row.task_number })}
            {row.exam_level ? ` · ${row.exam_level}` : ""}
          </span>
        </div>
        {pending ? (
          <Badge tone={urgency.tone} dot>
            {urgency.late ? t("promises.queue.lateBy", { hours: urgency.hours }) : t("promises.queue.hoursLeft", { hours: urgency.hours })}
          </Badge>
        ) : (
          <Badge tone="success">{t("promises.queue.doneBy", { name: row.done_by_name || "—", date: dateTimeText(row.done_at ?? "", i18n.language) })}</Badge>
        )}
      </div>
      <p className="muted">
        {t("promises.queue.aiMark", { mark: formatReviewMark(row.previous_score) })} · {dateTimeText(row.created_at, i18n.language)}
      </p>
      <p>{row.reason ? t("promises.queue.learnerReason", { reason: row.reason }) : t("promises.queue.noReason")}</p>
      {pending && canEdit ? (
        <>
          <p className="muted">{t("promises.queue.regrade")}</p>
          <TextAreaField label={t("promises.queue.comment")} value={comment} rows={3} onChange={(e) => setComment(e.target.value)} />
          <ErrorList error={error} />
          <div>
            <Button size="sm" loading={busy} disabled={!comment.trim()} onClick={() => void complete()}>
              {t("promises.queue.complete")}
            </Button>
          </div>
        </>
      ) : null}
      {!pending && row.trainer_comment ? <p className="muted">{row.trainer_comment}</p> : null}
    </li>
  );
}

export function ReviewRequestsPanel() {
  const { t } = useTranslation();
  const { exam } = useAdminExam();
  const queryClient = useQueryClient();
  const [done, setDone] = useState(false);
  const [page, setPage] = useState(1);
  const params: AcademyReviewRequestsListParams = { exam, status: done ? "done" : "pending", page };
  const list = useAcademyReviewRequestsList(params);
  const stats = useAcademyReviewRequestsStatsRetrieve();
  const data = list.data?.status === 200 ? list.data.data : undefined;
  const board = stats.data?.status === 200 ? stats.data.data : undefined;

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: getAcademyReviewRequestsListQueryKey() });
    await queryClient.invalidateQueries({ queryKey: getAcademyReviewRequestsStatsRetrieveQueryKey() });
  };

  return (
    <GlassCard as="section" className="stack" aria-labelledby="academy-review-requests">
      <div className="section-title">
        <div>
          <h2 id="academy-review-requests">{t("promises.queue.title")}</h2>
          <p className="muted">{t("promises.queue.intro", { hours: board?.deadline_hours ?? 72 })}</p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setDone((value) => !value);
            setPage(1);
          }}
        >
          {done ? t("promises.queue.showPending") : t("promises.queue.showDone")}
        </Button>
      </div>
      {board ? (
        <div className="kpis">
          <StatCard icon={<Hourglass size={18} />} label={t("promises.queue.pending")} value={board.pending} tone="accent" />
          <StatCard icon={<AlarmClock size={18} />} label={t("promises.queue.overdue")} value={board.overdue} tone={board.overdue ? "danger" : "success"} />
          <StatCard icon={<Timer size={18} />} label={t("promises.queue.dueSoon")} value={board.due_soon} tone={board.due_soon ? "warning" : "accent"} />
          <StatCard
            icon={<CheckCircle2 size={18} />}
            label={t("promises.queue.average")}
            value={board.average_hours === null ? t("promises.queue.none") : t("promises.queue.averageValue", { hours: board.average_hours })}
            hint={t("promises.queue.late30", { count: board.late_30_days })}
            tone="info"
          />
        </div>
      ) : null}
      {data && data.results.length ? (
        <>
          <ul className="stack review-requests">
            {data.results.map((row) => (
              <RequestRow key={row.id} row={row} onDone={refresh} />
            ))}
          </ul>
          <Pager page={page} hasNext={Boolean(data.next)} onChange={setPage} />
        </>
      ) : data ? (
        <p className="muted">{t("promises.queue.empty")}</p>
      ) : null}
    </GlassCard>
  );
}
